export const portalCopy = {
  nav: {
    logoAria: 'Chisme — inicio',
    panel: 'Panel',
    logout: 'Cerrar sesión',
  },
  login: {
    title: 'Registra tu negocio en Chisme',
    subtitle: 'Accede a tu panel de partner para añadir tu local y ver tus analíticas.',
    emailLabel: 'Email',
    emailPlaceholder: 'tu@negocio.com',
    passwordLabel: 'Contraseña',
    passwordPlaceholder: 'Tu contraseña',
    submit: 'Entrar al panel',
    invalidCredentials: 'Email o contraseña incorrectos.',
  },
  dashboard: {
    greeting: (name: string) => `Hola, ${name}`,
    emptyTitle: 'Aún no tienes ningún negocio',
    emptySubtitle: 'Añade tu local en unos minutos. Comprueba la disponibilidad en tu zona y actívalo cuando quieras.',
    addBusiness: 'Añadir negocio',
    yourBusiness: 'Tu negocio',
    status: {
      draft: 'Borrador',
      active: 'Activo',
      expired: 'Expirado',
      cancelled: 'Cancelación programada',
    },
    continueDraft: 'Continuar registro',
    viewAnalytics: 'Ver analíticas',
    viewDetail: 'Ver detalle',
    editProfile: 'Editar perfil',
    cancelSubscription: 'Cancelar suscripción',
    renewSubscription: 'Renovar suscripción',
    deleteBusiness: 'Eliminar negocio',
    deleteConfirm: '¿Eliminar este negocio y empezar de nuevo?',
    perMonth: '/mes',
    listingValidUntil: (date: string) => `Activo hasta el ${date}`,
    subscriptionCancelled: (date: string) =>
      `Suscripción cancelada. Tu perfil seguirá activo hasta el ${date}.`,
    subscriptionExpired: 'Tu periodo ha terminado. Renueva para volver a aparecer en la app.',
    cancelConfirm:
      '¿Cancelar la suscripción? Tu negocio seguirá visible en la app hasta el final del periodo pagado. No se renovará automáticamente.',
    noCoverPhoto: 'Sin foto',
  },
  wizard: {
    title: 'Añadir negocio',
    back: 'Volver al panel',
    steps: ['Básicos', 'Perfil', 'Disponibilidad', 'Activar'] as const,
    next: 'Continuar',
    back_step: 'Atrás',
    basics: {
      title: 'Encuentra tu negocio',
      subtitle: 'Búscalo en Google Maps o pega el enlace de tu ficha. Rellenamos los datos por ti.',
      reviewTitle: 'Revisa los datos',
      reviewSubtitle:
        'Comprueba que es el negocio correcto. Solo puedes ajustar el nombre y la categoría.',
      importedFromGoogle: 'Importado de Google Maps',
      changePlace: 'Cambiar negocio',
      categoryRequired: 'Selecciona la categoría que mejor describe tu negocio.',
      locationSyncNote:
        'La ciudad y la dirección se importan de Google Maps. Si las actualizas allí, el cambio se reflejará también en Chisme.',
      name: 'Nombre del negocio',
      namePlaceholder: 'Ej. Café Sol',
      city: 'Ciudad',
      category: 'Categoría',
      categoryPlaceholder: 'Selecciona categoría',
      address: 'Dirección',
      addressPlaceholder: 'Calle, número, código postal',
      categories: {
        food: 'Café y restaurante — 30 €/mes',
        wellness: 'Pilates, yoga y gimnasio — 50 €/mes',
      },
      google: {
        searchLabel: 'Buscar en Google Maps',
        searchPlaceholder: 'Ej. Café Sol, Yoga Gràcia…',
        searchHint: 'Escribe el nombre de tu local o estudio.',
        searchError: 'No pudimos buscar en Google Maps. Inténtalo de nuevo.',
        notFound: 'No encontramos ese negocio. Prueba con otro nombre o enlace.',
        invalidLink: 'Pega un enlace válido de Google Maps.',
        linkDivider: 'o',
        linkLabel: 'Enlace de Google Maps',
        linkPlaceholder: 'https://maps.google.com/…',
        linkHint: 'Copia el enlace desde la ficha de tu negocio en Google Maps.',
        importLink: 'Importar',
        importing: 'Importando…',
        mockHint:
          'Modo demo: prueba «Café Sol», «Yoga Gràcia», «La Latina» o pega un enlace de los resultados mock.',
      },
    },
    profile: {
      title: 'Perfil del negocio',
      subtitle: 'Esta información aparecerá en la app de Chisme.',
      subtitleFood: 'Completa la descripción y las fotos de tu local.',
      subtitleWellness: 'Añade las clases que ofreces. El horario se sincroniza desde Google Maps.',
      description: 'Descripción',
      descriptionPlaceholder: 'Describe tu local en pocas frases…',
      descriptionMaxHint: 'Máximo 300 caracteres.',
      descriptionCharCount: (count: number, max = 300) => `${count}/${max}`,
      photos: 'Fotos',
      photosHint: 'Arrastra imágenes aquí o haz clic para añadir (mín. 1, máx. 9)',
      photosImportedHint: 'Fotos importadas de Google. Puedes eliminarlas, reordenar o añadir más.',
      photosAddMore: 'Añadir fotos',
      photosRemove: 'Eliminar foto',
      photosSetCover: 'Hacer principal',
      photosCover: 'Principal',
      photosSelected: (count: number, max = 9) => `${count}/${max} fotos`,
      website: 'Sitio web',
      websitePlaceholder: 'https://',
      instagram: 'Instagram',
      instagramPlaceholder: '@tunegocio',
      hours: 'Horario',
      hoursFromGoogle: 'Horario de Google Maps',
      hoursSyncNote:
        'El horario se importa automáticamente de Google Maps. Si lo actualizas allí, el cambio se reflejará también en Chisme.',
      hoursToggleWellness: 'Mostrar horario de apertura en la app',
      tabs: {
        classes: 'Clases',
        hours: 'Horario',
      },
      days: {
        mon: 'Lunes',
        tue: 'Martes',
        wed: 'Miércoles',
        thu: 'Jueves',
        fri: 'Viernes',
        sat: 'Sábado',
        sun: 'Domingo',
      },
      closed: 'Cerrado',
      classes: {
        title: 'Clases',
        subtitle:
          'Añade hasta 3 clases que quieras destacar — las que más pueden interesar y atraer a nuevas clientas.',
        add: 'Añadir clase',
        remove: 'Eliminar',
        removeAria: (n: number) => `Eliminar clase ${n}`,
        empty: 'Aún no has añadido ninguna clase.',
        emptyAction: 'Añadir la primera clase',
        count: (current: number, max: number) => `${current}/${max}`,
        itemLabel: (n: number) => `Clase ${n}`,
        name: 'Nombre',
        nameRequired: 'Obligatorio',
        namePlaceholder: 'Ej. Pilates reformer, Yoga vinyasa',
        optionalSection: 'Horario y precio',
        optionalHint: 'opcional — ayuda a las usuarias a elegir',
        days: 'Días',
        daysPlaceholder: 'Lun - Vie, Sáb…',
        timeFrom: 'Desde',
        timeTo: 'Hasta',
        price: 'Precio',
        pricePlaceholder: '18',
        priceSuffix: '€',
      },
    },
    availability: {
      title: 'Disponibilidad',
      subtitle: 'Comprobamos si hay plaza en tu zona y categoría antes de activar.',
      detecting: 'Detectando zona a partir de la dirección…',
      neighbourhood: 'Zona detectada',
      slots: (occupied: number, max: number) => `${occupied} de ${max} plazas ocupadas`,
      available: 'Hay plaza disponible en tu zona',
      unavailable: 'Sin plazas disponibles en esta zona',
      unavailableHint: 'Prueba con otra dirección o categoría, o contacta con partnerships@chisme.social.',
      changeAddress: 'Cambiar dirección',
    },
    checkout: {
      title: 'Activar negocio',
      subtitle: 'Elige el periodo y revisa el resumen antes de activar tu perfil en la app.',
      previewLabel: 'Tu perfil en Chisme',
      planTitle: 'Elige tu periodo',
      plans: {
        monthly: {
          label: 'Mensual',
          hint: 'Sin permanencia. Cancela cuando quieras.',
        },
        quarterly: {
          label: '3 meses',
          badge: '−10 %',
          hint: 'Pago único por trimestre.',
        },
        semiannual: {
          label: '6 meses',
          badge: 'Mejor precio',
          hint: 'Pago único por semestre.',
        },
      },
      perMonth: (price: string) => `${price} €/mes`,
      savings: (amount: string) => `Ahorras ${amount} €`,
      includesTitle: 'Qué incluye',
      includes: [
        'Perfil destacado en la app',
        'Visibilidad en el mapa',
        'Recomendación al crear planes',
        'Panel de analíticas',
        'Exclusividad en tu barrio',
      ] as const,
      summary: 'Resumen',
      plan: 'Plan',
      period: 'Periodo',
      validUntil: 'Activo hasta',
      totalToday: 'Total hoy',
      paymentNote: 'Pago simulado en esta demo. En producción se cobrará con tarjeta.',
      monthlyFee: 'Cuota mensual',
      terms: 'He revisado el resumen y acepto activar mi negocio en Chisme.',
      activate: (total: string) => `Activar negocio — ${total} €`,
      processing: 'Procesando…',
    },
    success: {
      title: '¡Tu negocio está activo!',
      subtitle: 'Ya apareces en la app de Chisme cuando se crean planes en tu zona.',
      validUntil: (date: string) => `Tu listing está activo hasta el ${date}.`,
      cta: 'Ver analíticas',
    },
  },
  business: {
    back: 'Volver al panel',
    active: 'Activo',
    draft: 'Borrador',
    profileTitle: 'Perfil',
    kpis: {
      plans: 'Planes este mes',
      participants: 'Participantes estimados',
      profileViews: 'Vistas del perfil',
      mapViews: 'Vistas en el mapa',
      webClicks: 'Clics en web',
      instagramClicks: 'Clics en Instagram',
    },
    chartTitle: 'Planes por mes',
    editProfile: 'Editar perfil',
    profileSaved: 'Perfil actualizado. Los cambios ya se ven en la vista previa de la app.',
    subscriptionTitle: 'Suscripción',
    manageSubscription: 'Gestionar suscripción',
    subscriptionCancelled: 'Cancelación confirmada',
    edit: {
      title: 'Editar perfil',
      subtitle: 'Actualiza fotos, descripción, contacto y clases. La ubicación y el horario se sincronizan desde Google Maps.',
      back: 'Volver al negocio',
      save: 'Guardar cambios',
      cancel: 'Cancelar',
      locationSyncNote:
        'La ciudad y la dirección se sincronizan desde Google Maps. Para cambiarlas, actualiza tu ficha en Google.',
    },
  },
  preview: {
    websitePlaceholder: 'tunegocio.com',
    instagramPlaceholder: '@tunegocio',
    classesTitle: 'Clases',
    classesEmptyHint: 'Añade tus clases en el formulario',
    classPlaceholders: [
      { name: 'Yoga vinyasa', schedule: 'Lun - Vie · 09:00', price: '18' },
      { name: 'Pilates reformer', schedule: 'Mar y Jue · 18:30', price: '22' },
    ] as const,
  },
} as const;
